import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { WhoopStatus } from '@repo/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SpotifyIcon, StravaIcon, WhoopIcon } from '@/components/brand-icons';
import { cn } from '@/lib/utils';
import { getWhoopStatus, disconnectWhoop, WHOOP_CONNECT_URL } from '@/whoop/api';

interface Integration {
  key: string;
  name: string;
  description: string;
  Icon: ComponentType<{ className?: string }>;
  /** Brand-colored badge: background + glyph color. */
  badgeClassName: string;
}

const INTEGRATIONS: Integration[] = [
  {
    key: 'strava',
    name: 'Strava',
    description: 'Sync your runs, rides, and activities.',
    Icon: StravaIcon,
    badgeClassName: 'bg-[#FC4C02] text-white',
  },
  {
    key: 'spotify',
    name: 'Spotify',
    description: 'Show what you’re currently listening to.',
    Icon: SpotifyIcon,
    badgeClassName: 'bg-[#1DB954] text-white',
  },
  {
    key: 'whoop',
    name: 'Whoop',
    description: 'Pull in recovery, strain, and sleep.',
    Icon: WhoopIcon,
    badgeClassName: 'bg-white text-black',
  },
];

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [whoopOutcome, setWhoopOutcome] = useState<string | null>(null);

  // Surface the WHOOP OAuth result (?whoop=connected|error) once, then strip the
  // param so a refresh doesn't re-show the banner. The second run early-returns
  // because the param is gone, so this doesn't loop.
  useEffect(() => {
    const outcome = searchParams.get('whoop');
    if (!outcome) return;
    setWhoopOutcome(outcome);
    const next = new URLSearchParams(searchParams);
    next.delete('whoop');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    // The global shell (ww-root) is dark and sets light text; re-assert the
    // light theme's text color while this page still uses the light components.
    <main className="flex-1 bg-gradient-to-b from-background to-muted text-foreground">
      <div className="mx-auto w-full max-w-2xl px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your account and connected services.
          </p>
        </div>

        {whoopOutcome === 'connected' && (
          <p className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600">
            WHOOP connected — importing your history now. It may take a few minutes to appear.
          </p>
        )}
        {whoopOutcome === 'error' && (
          <p className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Couldn’t connect WHOOP. Please try again.
          </p>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Integrations</CardTitle>
            <CardDescription>Connect third-party services to sync your data.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {INTEGRATIONS.map((integration) =>
              integration.key === 'whoop' ? (
                <WhoopIntegrationRow key={integration.key} integration={integration} />
              ) : (
                <IntegrationRow
                  key={integration.key}
                  integration={integration}
                  action={
                    <Button variant="outline" size="sm" className="shrink-0">
                      Connect
                    </Button>
                  }
                />
              ),
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function IntegrationRow({
  integration,
  action,
  statusText,
  statusClassName,
}: {
  integration: Integration;
  action: ReactNode;
  statusText?: string;
  statusClassName?: string;
}) {
  const { name, description, Icon, badgeClassName } = integration;
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-background/40 p-4 transition-colors hover:bg-accent/40">
      <div className="flex min-w-0 items-center gap-4">
        <span
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-lg shadow-sm',
            badgeClassName,
          )}
        >
          <Icon className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium leading-none">{name}</p>
          <p className="mt-1 truncate text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {statusText ? (
          <span className={cn('text-xs font-medium', statusClassName)}>{statusText}</span>
        ) : null}
        {action}
      </div>
    </div>
  );
}

/** WHOOP row wired to the real connect/disconnect flow and live status. */
function WhoopIntegrationRow({ integration }: { integration: Integration }) {
  const [status, setStatus] = useState<WhoopStatus | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    getWhoopStatus()
      .then((s) => {
        if (active) setStatus(s);
      })
      .catch(() => {
        if (active) setStatus(null);
      });
    return () => {
      active = false;
    };
  }, []);

  const connected = status?.status === 'active';
  const needsReauth = status?.status === 'needs_reauth';

  // Top-level navigation (not fetch) so the browser follows WHOOP's OAuth 302.
  const connect = () => {
    window.location.href = WHOOP_CONNECT_URL;
  };
  const disconnect = async () => {
    setBusy(true);
    try {
      await disconnectWhoop();
      setStatus({ connected: false, status: 'revoked', lastSyncedAt: null });
    } catch {
      // Server-side disconnect failed — keep showing the real (still connected)
      // state so the user can retry.
    } finally {
      setBusy(false);
    }
  };

  const action = connected ? (
    <Button variant="outline" size="sm" className="shrink-0" disabled={busy} onClick={disconnect}>
      Disconnect
    </Button>
  ) : (
    <Button
      variant={needsReauth ? 'default' : 'outline'}
      size="sm"
      className="shrink-0"
      disabled={status === null}
      onClick={connect}
    >
      {needsReauth ? 'Reconnect' : 'Connect'}
    </Button>
  );

  return (
    <IntegrationRow
      integration={integration}
      action={action}
      statusText={connected ? 'Connected' : needsReauth ? 'Reconnect needed' : undefined}
      statusClassName={connected ? 'text-emerald-500' : 'text-amber-500'}
    />
  );
}
